import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FalkeTekLogo } from "./Brand";
import {
  Home, Send, Gift, User, Wrench, BarChart3, Briefcase,
  ShieldCheck, LogOut, Coins, Kanban, Users as UsersIcon,
  Building2, FileText, ScrollText, CalendarCheck, Inbox,
  GitFork, ShoppingBag, Trophy, Handshake, CalendarDays, HelpCircle,
} from "lucide-react";
import NotificationCenter from "./NotificationCenter";
import StufenaufstiegModal from "./StufenaufstiegModal";
import CoinEarnAnimation from "./CoinEarnAnimation";
import { api } from "../lib/api";

const NAV = {
  kunde: [
    { to: "/app", icon: Home, label: "Dashboard", end: true, id: "nav-dashboard" },
    { to: "/app/empfehlen", icon: Send, label: "Empfehlen", id: "nav-empfehlen" },
    { to: "/app/baum", icon: GitFork, label: "Baum", id: "nav-baum" },
    { to: "/app/trophaeen", icon: Gift, label: "Trophäen", id: "nav-trophaeen" },
    { to: "/app/shop", icon: ShoppingBag, label: "Shop", id: "nav-shop" },
    { to: "/app/partner", icon: Handshake, label: "Partner", id: "nav-partner" },
    { to: "/app/coins", icon: Coins, label: "Coin-Verlauf", id: "nav-coins" },
    { to: "/app/service", icon: Wrench, label: "Service", id: "nav-service" },
    { to: "/app/regeln", icon: HelpCircle, label: "Wie funktioniert's?", id: "nav-regeln" },
    { to: "/app/profil", icon: User, label: "Profil", id: "nav-profil" },
  ],
  kunde_gewerbe: [
    { to: "/app/tippgeber", icon: Briefcase, label: "Cockpit", end: true, id: "nav-cockpit" },
    { to: "/app/empfehlen", icon: Send, label: "Empfehlen", id: "nav-empfehlen" },
    { to: "/app/baum", icon: GitFork, label: "Baum", id: "nav-baum" },
    { to: "/app/shop", icon: ShoppingBag, label: "Shop", id: "nav-shop" },
    { to: "/app/partner", icon: Handshake, label: "Partner", id: "nav-partner" },
    { to: "/app/service", icon: Wrench, label: "Service", id: "nav-service" },
    { to: "/app/regeln", icon: HelpCircle, label: "Wie funktioniert's?", id: "nav-regeln" },
    { to: "/app/profil", icon: User, label: "Profil", id: "nav-profil" },
  ],
  vertriebler: [
    { to: "/vertriebler", icon: Kanban, label: "Pipeline", end: true, id: "nav-pipeline" },
    { to: "/vertriebler/kalender", icon: CalendarDays, label: "Kalender", id: "nav-kalender" },
    { to: "/vertriebler/leaderboard", icon: Trophy, label: "Leaderboard", id: "nav-vlb" },
    { to: "/vertriebler/stats", icon: BarChart3, label: "Statistik", id: "nav-stats" },
    { to: "/vertriebler/profil", icon: User, label: "Profil", id: "nav-vprofil" },
  ],
  admin: [
    { to: "/admin", icon: ShieldCheck, label: "Übersicht", end: true, id: "nav-admin" },
    { to: "/admin/signups", icon: Inbox, label: "Self-Signups", id: "nav-signups" },
    { to: "/admin/gewerbe", icon: Building2, label: "Gewerbe", id: "nav-gewerbe" },
    { to: "/admin/empfehlungen", icon: Kanban, label: "Pipeline", id: "nav-apipeline" },
    { to: "/admin/kalender", icon: CalendarDays, label: "Kalender", id: "nav-akalender" },
    { to: "/admin/service", icon: Wrench, label: "Service", id: "nav-aservice" },
    { to: "/admin/vollzahlungen", icon: CalendarCheck, label: "Vollzahlung", id: "nav-vollzahlung" },
    { to: "/admin/provisionen", icon: FileText, label: "Provisionen", id: "nav-provisionen" },
    { to: "/admin/gutschriften", icon: ScrollText, label: "Gutschriften", id: "nav-gutschriften" },
    { to: "/admin/geschenke", icon: Gift, label: "Geschenke", id: "nav-geschenke" },
    { to: "/admin/shop", icon: ShoppingBag, label: "Shop", id: "nav-adminshop" },
    { to: "/admin/partner", icon: Handshake, label: "Partner", id: "nav-apartner" },
    { to: "/admin/users", icon: UsersIcon, label: "Nutzer", id: "nav-users" },
    { to: "/admin/leaderboard", icon: Trophy, label: "Leaderboard", id: "nav-alb" },
  ],
  buchhalter: [
    { to: "/admin", icon: ShieldCheck, label: "Übersicht", end: true, id: "nav-admin" },
    { to: "/admin/vollzahlungen", icon: CalendarCheck, label: "Vollzahlung", id: "nav-vollzahlung" },
    { to: "/admin/provisionen", icon: FileText, label: "Provisionen", id: "nav-provisionen" },
    { to: "/admin/gutschriften", icon: ScrollText, label: "Gutschriften", id: "nav-gutschriften" },
    { to: "/admin/service", icon: Wrench, label: "Service", id: "nav-aservice" },
    { to: "/admin/users", icon: UsersIcon, label: "Nutzer", id: "nav-users" },
  ],
};

function navKey(user) {
  if (user.role === "kunde") return user.tippgeber_modus === "gewerblich_aktiv" ? "kunde_gewerbe" : "kunde";
  return user.role;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[navKey(user)] || [];
  const [notifications, setNotifications] = useState([]);

  // Polling notifications zentral, damit Animationen Bescheid wissen
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.get("/me/notifications");
        if (!stop) setNotifications(r.data || []);
      } catch {}
    };
    tick();
    const t = setInterval(tick, 25000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-falke-warmwhite flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-falke-blue/10 shadow-sm">
        <div className="p-5 border-b border-falke-blue/10">
          <FalkeTekLogo height={42} showSlogan={false} />
          <div className="mt-2 text-[10px] tracking-[0.16em] uppercase font-semibold text-falke-orange">FalkeCoin</div>
          <div className="text-[11px] text-falke-blue/70 leading-snug">Empfehlen. Profitieren.<br/>Energie teilen.</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-falke-orange text-white" : "text-falke-blue hover:bg-falke-orange/10"
                }`
              }
            >
              <it.icon size={18} strokeWidth={1.7} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-falke-blue/10">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-falke-blue/60 uppercase tracking-wider">Angemeldet als</div>
            <div className="font-semibold text-falke-blue text-sm truncate">{user.name}</div>
            <div className="text-xs text-falke-anthracite/60 truncate">{user.email}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-falke-blue hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut size={16} /> Abmelden
          </button>
          <div className="flex justify-center gap-3 mt-3 text-[11px] text-falke-blue/50">
            <a href="/impressum" target="_blank" rel="noreferrer" className="hover:text-falke-orange" data-testid="footer-impressum">Impressum</a>
            <span>·</span>
            <a href="/datenschutz" target="_blank" rel="noreferrer" className="hover:text-falke-orange" data-testid="footer-datenschutz">Datenschutz</a>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 bg-white border-b border-falke-blue/10 z-40 flex items-center justify-between px-4 py-3">
        <FalkeTekLogo height={30} showSlogan={false} />
        <div className="flex items-center gap-2">
          <NotificationCenter />
          <button onClick={handleLogout} className="text-falke-blue" data-testid="mobile-logout-btn">
            <LogOut size={20} />
          </button>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-16 md:pt-0 md:pb-0 bottom-nav-padding md:bottom-nav-padding-none">
        {/* Desktop top-right notification bell */}
        <div className="hidden md:flex justify-end px-8 pt-4">
          <NotificationCenter />
        </div>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-falke-blue/10 z-40 flex justify-around items-center py-2 pb-[calc(8px+env(safe-area-inset-bottom))]">
        {items.slice(0, 5).map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`m-${it.id}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-1 px-2 text-[10px] font-medium ${
                isActive ? "text-falke-orange" : "text-falke-blue/70"
              }`
            }
          >
            <it.icon size={20} strokeWidth={1.7} />
            {it.label}
          </NavLink>
        ))}
      </nav>

      {/* Global Wow-Moments */}
      {user?.role === "kunde" && (
        <>
          <StufenaufstiegModal />
          <CoinEarnAnimation notifications={notifications} />
        </>
      )}
    </div>
  );
}
